/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — inline-editable client attach chip.

   Same interaction as the classic editor's identity-row EditableAttach
   (app.tsx): reads as quiet text, click turns it into a searchable
   dropdown (the kit's SearchableSelect — no plain <select>, per
   DESIGN.md §5). Kept local rather than extracted from app.tsx so the
   frozen classic editor stays untouched (contract fence).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect';
import { Icon } from '../components/ui/Icon';
import { rem } from '../lib/rem';

export function AttachChip({
  value,
  options,
  placeholder,
  emptyLabel,
  title,
  onChange,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  emptyLabel: string;
  title: string;
  onChange: (v: string) => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!editing) return;
    const onDown = (e: MouseEvent) => {
      let n = e.target as HTMLElement | null;
      while (n) {
        if (n === wrapRef.current || n.getAttribute?.('role') === 'listbox') return;
        n = n.parentElement;
      }
      setEditing(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [editing]);

  if (editing) {
    return (
      <div ref={wrapRef} style={{ minWidth: rem(200) }}>
        <SearchableSelect
          value={value || ''}
          options={options}
          placeholder={placeholder}
          searchPlaceholder={placeholder}
          clearable
          onChange={(v) => {
            onChange(v);
            setEditing(false);
          }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title={title}
      className={`sfs-attach-chip${value ? '' : ' is-empty'}`}
    >
      {value || emptyLabel}
      <Icon icon={ChevronDown} size="md" aria-hidden="true" style={{ opacity: 0.5 }} />
    </button>
  );
}
